<script setup lang="ts">
/**
 * SNSシェアボタンコンポーネント
 * Line, X (Twitter), Facebookに対応
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
    shareUrl: string;
    shareText: string;
    hashTags?: string[];
}>();

const { t } = useI18n();

// URLエンコードヘルパー
const encode = (str: string) => encodeURIComponent(str);

// ハッシュタグの処理
const formattedHashTags = computed(() => {
    return props.hashTags ? props.hashTags.join(',') : '';
});

// 各SNSのシェアURLの生成
const xShareUrl = computed(() => {
    const url = encode(props.shareUrl);
    const text = encode(props.shareText);
    const tags = encode(formattedHashTags.value);
    return `https://twitter.com/intent/tweet?text=${text}&url=${url}&hashtags=${tags}`;
});

const lineShareUrl = computed(() => {
    // LineはテキストとURLを併記してメッセージとして送る形式が一般的
    const text = encode(`${props.shareText} ${props.shareUrl}`);
    return `https://line.me/R/msg/text/?${text}`;
});

const facebookShareUrl = computed(() => {
    const url = encode(props.shareUrl);
    return `https://www.facebook.com/sharer/sharer.php?u=${url}`;
});

// ウィンドウを開く関数
const openShareWindow = (url: string) => {
    window.open(url, '_blank', 'width=600,height=400,resizable=yes,scrollbars=yes');
};
</script>

<template>
    <div class="share-buttons">
        <button @click="openShareWindow(xShareUrl)" class="share-btn x-btn" :aria-label="t('share.x')">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path
                    d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
        </button>
        <button @click="openShareWindow(lineShareUrl)" class="share-btn line-btn" :aria-label="t('share.line')">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path
                    d="M22 10.8c0-4.8-4.5-8.8-10-8.8S2 6 2 10.8c0 4.3 3.7 7.9 8.6 8.6.4.1.8.2 1 .5.1.3.1.8 0 1.3-.1.7-.6 2.6-.8 3.2-.1.5.4.9.9.5 3.8-2.8 7.3-5.4 8-6.3.9-1 1.8-3.7 1.8-6.8z" />
                <!-- Simplified Line Icon Path -->
            </svg>
        </button>
        <button @click="openShareWindow(facebookShareUrl)" class="share-btn facebook-btn" :aria-label="t('share.facebook')">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path
                    d="M22.676 0H1.324C.593 0 0 .593 0 1.324v21.352C0 23.408.593 24 1.324 24h11.494v-9.294H9.689v-3.621h3.129V8.41c0-3.099 1.894-4.785 4.659-4.785 1.325 0 2.464.097 2.796.141v3.24h-1.921c-1.5 0-1.792.721-1.792 1.771v2.311h3.584l-.465 3.63H16.56V24h6.115c.733 0 1.325-.592 1.325-1.324V1.324C24.001.593 23.408 0 22.676 0z" />
            </svg>
        </button>
    </div>
</template>

<style scoped>
.share-buttons {
    display: flex;
    gap: 1rem;
    justify-content: center;
    margin-top: 1rem;
}

.share-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    border: none;
    cursor: pointer;
    background-color: #f0f0f0;
    transition: transform 0.2s, box-shadow 0.2s;
    color: white;
}

.share-btn:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
}

.x-btn {
    background-color: #000;
}

.line-btn {
    background-color: #06C755;
}

.facebook-btn {
    background-color: #1877F2;
}
</style>
